import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, ShieldCheck, MessageSquare, Package, Sparkles, Plane, Train, Car, Ship, Bus } from "lucide-react";
import { useState } from "react";
import CityAutocomplete from "@/components/travel/CityAutocomplete";
import PublicFeed from "@/components/travel/PublicFeed";
import EliteLeaderboard from "@/components/travel/EliteLeaderboard";

export default function Landing() {
  const nav = useNavigate();
  const [from, setFrom] = useState({ label: "" });
  const [to, setTo] = useState({ label: "" });

  const goSearch = () => {
    const p = new URLSearchParams();
    if (from.label) p.set("origin", from.label);
    if (to.label) p.set("destination", to.label);
    nav(`/search?${p.toString()}`);
  };

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1532968899863-5b52ef155913?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxhaXJwb3J0JTIwdHJhdmVsZXIlMjBsdWdnYWdlfGVufDB8fHx8MTc4OTMyNjQ0MXww&ixlib=rb-4.1.0&q=85')", backgroundSize: "cover", backgroundPosition: "center", filter: "brightness(0.5)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#020617]/70 to-[#020617]" />
        <div className="relative max-w-6xl mx-auto px-4 pt-16 pb-20 md:pt-24 md:pb-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-xs text-slate-300 mb-6">
              <Sparkles size={12} className="text-[#F59E0B]" /> Première plateforme mondiale voyageurs ↔ buddies
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
              L'annuaire<br />
              <span className="text-gradient">des voyageurs</span>
            </h1>
            <p className="mt-5 text-slate-300 max-w-xl text-base sm:text-lg">
              Trouvez un voyageur qui part au bon endroit, au bon moment. Débloquez le contact pour 2€ et confiez votre colis en toute confiance.
            </p>
          </motion.div>

          {/* Search bloc */}
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-10 glass-strong rounded-2xl p-4 md:p-5 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3"
          >
            <CityAutocomplete testid="hero-origin" value={from.label} onChange={setFrom} placeholder="Ville de départ" />
            <CityAutocomplete testid="hero-destination" value={to.label} onChange={setTo} placeholder="Ville d'arrivée" />
            <button data-testid="hero-search-btn" onClick={goSearch} className="btn-coral px-6 py-2.5 rounded-lg flex items-center justify-center gap-2">
              <Search size={16} /> Chercher
            </button>
          </motion.div>

          <div className="mt-6 flex flex-wrap gap-2 text-xs text-slate-400">
            {[["Avion", Plane],["Train", Train],["Route", Car],["Bateau", Ship],["Bus", Bus]].map(([k, Ic]) => (
              <span key={k} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900/60 border border-slate-800">
                <Ic size={12} /> {k}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="font-display text-3xl md:text-4xl font-bold mb-10">Comment ça marche</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="glass rounded-2xl p-6">
            <div className="text-[#FF6B6B] font-semibold mb-2 flex items-center gap-2"><Plane size={16}/> Voyageurs</div>
            <ol className="space-y-4 text-sm">
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-xs">1</span><span>Publiez votre trajet en <b>6 étapes guidées</b> (mode, lieux, dates, photo billet, exclusions).</span></li>
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-xs">2</span><span>Recevez des demandes de buddies vérifiés via la messagerie interne.</span></li>
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-xs">3</span><span>Encaissez via <b>séquestre sécurisé</b> à la livraison.</span></li>
            </ol>
            <Link to="/wizard" data-testid="landing-cta-publish" className="mt-6 inline-flex items-center gap-2 btn-coral px-4 py-2 rounded-lg text-sm">
              Publier un trajet <ArrowRight size={14} />
            </Link>
          </div>
          <div className="glass rounded-2xl p-6">
            <div className="text-[#3B82F6] font-semibold mb-2 flex items-center gap-2"><Package size={16}/> Buddies</div>
            <ol className="space-y-4 text-sm">
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-xs">1</span><span>Cherchez un trajet en <b>3 clics</b> (départ, arrivée, dates).</span></li>
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-xs">2</span><span>Débloquez le contact pour <b>2€</b>, ou via abonnement / pub.</span></li>
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-xs">3</span><span>Discutez, sécurisez la mission, notez le voyageur.</span></li>
            </ol>
            <Link to="/search" data-testid="landing-cta-search" className="mt-6 inline-flex items-center gap-2 btn-ghost-glass px-4 py-2 rounded-lg text-sm">
              Chercher maintenant <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-6xl mx-auto px-4 pb-16 grid md:grid-cols-3 gap-6">
        {[
          { Ic: ShieldCheck, t: "Séquestre Stripe", d: "Fonds gelés, libérés uniquement à la livraison confirmée." },
          { Ic: MessageSquare, t: "Chat sécurisé", d: "Numéros et emails masqués automatiquement dans les messages." },
          { Ic: Sparkles, t: "Profils notés", d: "Avis mutuels et badges de vérification (email, ID, téléphone)." },
        ].map((f) => (
          <div key={f.t} className="glass rounded-2xl p-6">
            <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center mb-3">
              <f.Ic size={18} className="text-[#F59E0B]" />
            </div>
            <div className="font-semibold">{f.t}</div>
            <p className="text-sm text-slate-400 mt-1">{f.d}</p>
          </div>
        ))}
      </section>

      <PublicFeed />
      <EliteLeaderboard />
    </div>
  );
}
